import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  AiClassFacetsResponse,
  DataAcquisitionDetail,
  DataAcquisitionImage,
  DataPointList,
  DataPointFacetsResponse,
  DataPointFilterCountUnit,
  DataPointFilterSelection,
  DefectTypeFacetsResponse,
  UpdateAssigneeBody,
  UpdateDataPointStatusBody,
  UpdateDataPointStatusResponse,
} from "@aeriosiq/types";
import { useAiFilterStore } from "./state/aiFilterStore.js";
import { currentRevision, tagRevision } from "./filterRevision.js";

/**
 * The all-zero (nil) UUID used as a sentinel/default data-acquisition id
 * before the real id has hydrated. The DA detail endpoint 404s on it, so
 * no DA-scoped query may fire while the id is still this placeholder.
 */
export const NIL_DA_ID = "00000000-0000-0000-0000-000000000000";

/**
 * ANA-R2-04 — true only for a defined, non-empty DA id that is NOT the
 * all-zero sentinel. `enabled` guards on DA-scoped queries must use this
 * (a bare `id.length > 0` lets the 36-char nil UUID through and fires a
 * guaranteed-404 `GET /data-acquisitions/0000…`).
 */
export function isRealDaId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.length > 0 && id !== NIL_DA_ID;
}

export const analysisKeys = {
  all: ["analysis"] as const,
  detail: (id: string) => [...analysisKeys.all, "detail", id] as const,
  image: (id: string) => [...analysisKeys.all, "image", id] as const,
  // SB6-ANA-FILT-05 — the data-point set + faceted counts. The set key
  // is parameterised by the 7-axis selection so widgets reading the same
  // selection share one fetch; the facets key additionally carries the
  // count unit (a settings change re-fires only the facets query).
  dataPoints: (id: string) => [...analysisKeys.all, "data-points", id] as const,
  dataPointsList: (id: string, selectionKey: string) =>
    [...analysisKeys.dataPoints(id), "list", selectionKey] as const,
  dataPointFacets: (id: string, selectionKey: string, countUnit: string) =>
    [
      ...analysisKeys.dataPoints(id),
      "facets",
      selectionKey,
      countUnit,
    ] as const,
  // SB6-ANA-FILT-06 (Track B) — Defect Filter Widget per-type facets.
  // Scoped to L1+L2 only (status + reviewState) plus the includeEmpty
  // toggle; lower levels do not narrow this endpoint (the widget IS the
  // defect-type level).
  defectTypeFacets: (id: string, scopeKey: string) =>
    [...analysisKeys.dataPoints(id), "defect-type-facets", scopeKey] as const,
  // SB6-ANA-FILT-06 (Track C) — AI Filter Module per-class facets. Like
  // the defect-type facets, scoped to L1+L2 only plus the includeEmpty
  // toggle; the per-class confidence histogram drives the client-side
  // ≥-threshold counts (D8), so this endpoint carries NO confidence param.
  aiClassFacets: (id: string, scopeKey: string) =>
    [...analysisKeys.dataPoints(id), "ai-class-facets", scopeKey] as const,
};

export function useDataAcquisitionDetailQuery(id: string) {
  const api = useApi();
  return useQuery<DataAcquisitionDetail>({
    queryKey: analysisKeys.detail(id),
    // ANA-R2-04 — only fetch once a real (non-empty, non-nil) DA id exists.
    // The all-zero sentinel id 404s, so it must never reach the network.
    enabled: isRealDaId(id),
    queryFn: async () => {
      const response = await api.get<DataAcquisitionDetail>(
        `/data-acquisitions/${encodeURIComponent(id)}`,
      );
      return response.data;
    },
  });
}

/**
 * SB6-ANA-HEADER-02 — self-unassign ("Release me") from the analysis
 * surface's burger menu. PATCHes `/data-acquisitions/:id/assignee` with
 * `userId: null`; the BE write is STATE-AWARE (clears prescreener_user_id
 * in PRE_SCREENING, expert_user_id otherwise) and the self-unassign
 * role-gate exists server-side (R58-W-09 / lyse 5.5.0).
 *
 * Invalidates the DA detail (so the header's assignee-derived gating
 * re-renders) plus the list cache (the Assignee column there shows the
 * same slot). The caller wires its own snackbar via the mutation's
 * onSuccess/onError options.
 */
export function useSelfUnassignMutation(dataAcquisitionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DataAcquisitionDetail>({
    mutationFn: async () => {
      const body: UpdateAssigneeBody = { userId: null };
      const response = await api.patch<DataAcquisitionDetail>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/assignee`,
        body,
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: analysisKeys.detail(dataAcquisitionId),
      });
      // The Data Acquisitions list Assignee column reads the same slot.
      void qc.invalidateQueries({ queryKey: ["data-acquisitions"] });
    },
  });
}

// ---------------------------------------------------------------------------
// SB6-ANA-FILT-05 — Global Data Point Filter (lyse/v1@7.0.0).
// ---------------------------------------------------------------------------

/**
 * Serialise a selection axis to the CSV the wire expects. Empty array ⇒
 * `undefined` (param omitted). The exception: `status` defaults to
 * ACTIVE-only when the array is empty, but the FE always sends an
 * explicit value (the default selection is `status: ["ACTIVE"]`), so we
 * keep the generic "empty ⇒ omit" rule and rely on the caller's default.
 */
function csv(values: readonly string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(",");
}

/**
 * Build the query-param object for the data-points + facets endpoints
 * from a 7-axis selection. `severity` is numeric on the wire but stored
 * as strings in the selection — passed straight through (the BE pattern
 * accepts integer strings).
 */
export function selectionToParams(
  selection: DataPointFilterSelection,
): Record<string, string> {
  const params: Record<string, string> = {};
  const status = csv(selection.status);
  if (status !== undefined) params.status = status;
  const reviewState = csv(selection.reviewState);
  if (reviewState !== undefined) params.reviewState = reviewState;
  const kind = csv(selection.kind);
  if (kind !== undefined) params.kind = kind;
  const defectTypeIds = csv(selection.defectType);
  if (defectTypeIds !== undefined) params.defectTypeIds = defectTypeIds;
  const componentIds = csv(selection.component);
  if (componentIds !== undefined) params.componentIds = componentIds;
  const location = csv(selection.location);
  if (location !== undefined) params.location = location;
  const severity = csv(selection.severity);
  if (severity !== undefined) params.severity = severity;
  return params;
}

/**
 * A stable cache key for a selection — order-independent within each
 * axis so `["ACTIVE","INACTIVE"]` and `["INACTIVE","ACTIVE"]` hit the
 * same cache entry.
 */
export function selectionCacheKey(selection: DataPointFilterSelection): string {
  const sortedCsv = (v: readonly string[]) => [...v].sort().join(",");
  return [
    sortedCsv(selection.status),
    sortedCsv(selection.reviewState),
    sortedCsv(selection.kind),
    sortedCsv(selection.defectType),
    sortedCsv(selection.component),
    sortedCsv(selection.location),
    sortedCsv(selection.severity),
  ].join("|");
}

/**
 * SB6-ANA-FILT-05 — the filtered data-point set every Analysis widget
 * reduces to. `GET …/{id}/data-points`. Works in LOCKED state (D12).
 *
 * @param pageSize when caller only needs the `total` (e.g. the progress
 *   bar's ACTIVE denominator), pass `1` to keep the payload tiny.
 */
export function useDataPointsQuery(
  id: string,
  selection: DataPointFilterSelection,
  opts?: { pageSize?: number; enabled?: boolean },
): UseQueryResult<DataPointList> {
  const api = useApi();
  const params = selectionToParams(selection);
  const pageSize = opts?.pageSize ?? 500;
  // SB6-ANA-FILT-06 (Track C / D6) — the committed AI sub-filter narrows
  // the global data-point set when the AI kind is active in the PASSED
  // selection. Folded into the params + cache key so every data-points
  // consumer reflects it without knowing about the AI widget. SB6-ANA-FILT-07
  // — the findings List/Tiles/viewer queries reuse the same committed value
  // via `useEffectiveAiClassFilter` (store-driven gate) so all surfaces agree.
  const aiClassFilter = useAiFilterStore((s) => s.committedFilter);
  const effectiveAi = selection.kind.includes("ai") ? aiClassFilter : null;
  return useQuery<DataPointList>({
    queryKey: [
      ...analysisKeys.dataPointsList(id, selectionCacheKey(selection)),
      pageSize,
      effectiveAi ?? "",
    ],
    enabled: id.length > 0 && opts?.enabled !== false,
    staleTime: 15_000,
    // ANA-R1-08 — keep the previous data-point set visible while the next
    // selection's set is in flight. Without this the query flipped to
    // `pending` with `data: undefined` on every global-filter change, so
    // `useFilteredDataPoints` momentarily returned an empty/loading set;
    // the Filmstrip then dropped the active image out of its filtered list
    // and re-`setActive`d the first image, whose `scrollIntoView` yanked the
    // shared `<main>` scroll container to the top of the canvas (the
    // "everything jumps up" the user reported). With keepPreviousData the
    // set never empties mid-change, the active image survives the refetch,
    // and the page scroll stays put. Consistent with the findings/defects
    // list queries (which already keep previous data) and the facet queries.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      // SB6-ANA-PROG-02 — capture the revision in effect at fetch time so
      // the payload can be matched against the latest revision; pass the
      // TanStack Query `signal` so a superseded revision's request aborts
      // (latest-wins) instead of completing out of order.
      const revision = currentRevision();
      const response = await api.get<DataPointList>(
        `/data-acquisitions/${encodeURIComponent(id)}/data-points`,
        {
          signal,
          params: {
            page: 1,
            pageSize,
            ...params,
            ...(effectiveAi !== null ? { aiClassFilter: effectiveAi } : {}),
          },
        },
      );
      return tagRevision(response.data, revision);
    },
  });
}

/**
 * SB6-ANA-FILT-05 — per-level facet counts driving the 7-level widget.
 * `GET …/{id}/data-points/facets`. Each level's option counts respect
 * only the selections of the levels above it (strict L1→L7 cascade);
 * counts are expressed in `countUnit`.
 */
export function useDataPointFacetsQuery(
  id: string,
  selection: DataPointFilterSelection,
  countUnit: DataPointFilterCountUnit,
  opts?: { enabled?: boolean },
): UseQueryResult<DataPointFacetsResponse> {
  const api = useApi();
  const params = selectionToParams(selection);
  // SB6-ANA-FILT-06 (Track C / D6) — the committed AI sub-filter makes the
  // L3 AI bracket count + the downstream L4–L6 cascade reflect the active
  // AI config; only applied when the AI kind is active in the PASSED selection.
  const aiClassFilter = useAiFilterStore((s) => s.committedFilter);
  const effectiveAi = selection.kind.includes("ai") ? aiClassFilter : null;
  return useQuery<DataPointFacetsResponse>({
    queryKey: [
      ...analysisKeys.dataPointFacets(
        id,
        selectionCacheKey(selection),
        countUnit,
      ),
      effectiveAi ?? "",
    ],
    enabled: id.length > 0 && opts?.enabled !== false,
    staleTime: 15_000,
    // SB6-ANA-FILT-06 (D14) — keep the previous facets payload visible
    // while the next selection's counts are in flight, so pressing an
    // option updates numbers in place instead of clearing + rebuilding the
    // widget (which caused the grey-frame height to jump). The FilterWidget
    // also reserves layout height to make the strip dimensionally stable.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const revision = currentRevision();
      const response = await api.get<DataPointFacetsResponse>(
        `/data-acquisitions/${encodeURIComponent(id)}/data-points/facets`,
        {
          signal,
          params: {
            countUnit,
            ...params,
            ...(effectiveAi !== null ? { aiClassFilter: effectiveAi } : {}),
          },
        },
      );
      return tagRevision(response.data, revision);
    },
  });
}

/**
 * SB6-ANA-FILT-06 (Track B / D10) — per-defect-type facets driving the
 * Defect Filter Widget. `GET …/{id}/defect-type-facets`. One row per leaf
 * defect catalogue entry with its L1+L2-scoped defect count and (when the
 * DA's class model ≠ None) its per-grade severity distribution.
 *
 * Only L1 (status) + L2 (reviewState) of the global selection narrow the
 * counts — the widget *is* the defect-type level, so lower axes don't
 * apply here. `includeEmpty=true` reveals catalogue types with 0 defects
 * (the widget's "show all catalogue types" toggle).
 */
export function useDefectTypeFacetsQuery(
  id: string,
  selection: DataPointFilterSelection,
  opts?: { includeEmpty?: boolean; enabled?: boolean },
): UseQueryResult<DefectTypeFacetsResponse> {
  const api = useApi();
  const includeEmpty = opts?.includeEmpty ?? false;
  const params: Record<string, string> = {};
  const status = csv(selection.status);
  if (status !== undefined) params.status = status;
  const reviewState = csv(selection.reviewState);
  if (reviewState !== undefined) params.reviewState = reviewState;
  if (includeEmpty) params.includeEmpty = "true";
  // L1+L2 scope key — order-independent within each axis, plus the toggle.
  const scopeKey = [
    [...selection.status].sort().join(","),
    [...selection.reviewState].sort().join(","),
    includeEmpty ? "all" : "present",
  ].join("|");
  return useQuery<DefectTypeFacetsResponse>({
    queryKey: analysisKeys.defectTypeFacets(id, scopeKey),
    enabled: id.length > 0 && opts?.enabled !== false,
    staleTime: 15_000,
    // Keep the previous rows visible while the next L1+L2 scope is in
    // flight so the table updates counts in place (no clear + rebuild).
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const revision = currentRevision();
      const response = await api.get<DefectTypeFacetsResponse>(
        `/data-acquisitions/${encodeURIComponent(id)}/defect-type-facets`,
        { signal, params },
      );
      return tagRevision(response.data, revision);
    },
  });
}

/**
 * SB6-ANA-FILT-06 (Track C / D8) — per-AI-class facets driving the AI
 * Filter Module. `GET …/{id}/ai-class-facets`. One row per AI class
 * present on the DA's images with its L1+L2-scoped finding count and a
 * **confidence histogram**. The FE computes the per-class slider's live
 * "# findings ≥ threshold" client-side from the cumulative histogram so
 * the slider is instant (D8); NO confidence param is sent here.
 *
 * Like the defect-type facets, only L1 (status) + L2 (reviewState) of the
 * global selection narrow the counts; lower axes do not apply (the widget
 * IS the AI sub-filter). `includeEmpty=true` reveals every class in the
 * model(s) even with 0 findings ("show all classes" toggle).
 */
export function useAiClassFacetsQuery(
  id: string,
  selection: DataPointFilterSelection,
  opts?: { includeEmpty?: boolean; enabled?: boolean },
): UseQueryResult<AiClassFacetsResponse> {
  const api = useApi();
  const includeEmpty = opts?.includeEmpty ?? false;
  const params: Record<string, string> = {};
  const status = csv(selection.status);
  if (status !== undefined) params.status = status;
  const reviewState = csv(selection.reviewState);
  if (reviewState !== undefined) params.reviewState = reviewState;
  if (includeEmpty) params.includeEmpty = "true";
  // L1+L2 scope key — order-independent within each axis, plus the toggle.
  const scopeKey = [
    [...selection.status].sort().join(","),
    [...selection.reviewState].sort().join(","),
    includeEmpty ? "all" : "present",
  ].join("|");
  return useQuery<AiClassFacetsResponse>({
    queryKey: analysisKeys.aiClassFacets(id, scopeKey),
    enabled: id.length > 0 && opts?.enabled !== false,
    staleTime: 15_000,
    // Keep the previous rows + histograms visible while the next L1+L2
    // scope is in flight so the table updates in place (no clear+rebuild).
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const revision = currentRevision();
      const response = await api.get<AiClassFacetsResponse>(
        `/data-acquisitions/${encodeURIComponent(id)}/ai-class-facets`,
        { signal, params },
      );
      return tagRevision(response.data, revision);
    },
  });
}

/**
 * SB6-ANA-FILT-05 (D5) — bulk ACTIVE/INACTIVE status mutation powering
 * the Filmstrip Activate/Deactivate buttons. `PATCH …/{id}/data-points/
 * status`. On success invalidates the data-points + facets queries so
 * every widget + the filter strip recompute. 409 when the DA is LOCKED
 * (the FE gates the buttons before the call — D12).
 */
export function useUpdateDataPointStatusMutation(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    UpdateDataPointStatusResponse,
    unknown,
    UpdateDataPointStatusBody
  >({
    mutationFn: async (body) => {
      const response = await api.patch<UpdateDataPointStatusResponse>(
        `/data-acquisitions/${encodeURIComponent(id)}/data-points/status`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: analysisKeys.dataPoints(id) });
    },
  });
}

// SYNC: backend-requirement 008-get-inspection-image
/**
 * @deprecated since lyse contract 1.3.0 — replaced by `useImagesQuery`
 * (see Story 012). The singular `getInspectionImage` endpoint is kept
 * in the contract until 2.0.0 for backwards compatibility but no FE
 * caller remains. Removed when the contract drops.
 */
export function useDataAcquisitionImageQuery(id: string) {
  const api = useApi();
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- intentional, see jsdoc above
  return useQuery<DataAcquisitionImage>({
    queryKey: analysisKeys.image(id),
    enabled: id.length > 0,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- intentional, see jsdoc above
      const response = await api.get<DataAcquisitionImage>(
        `/data-acquisitions/${encodeURIComponent(id)}/image`,
      );
      return response.data;
    },
  });
}
